import { createSelector } from '@reduxjs/toolkit';
import filter from 'lodash/fp/filter';
import first from 'lodash/fp/first';
import last from 'lodash/fp/last';

import { alertsAdapter } from './alertsSlice';
import { AlertContext, isAlertMatchingContext, isBackendErrorAlert, isDialogAlert, isSnackBarAlert } from './types';

import type { RootState } from 'store';

const alertsSelectors = alertsAdapter.getSelectors<RootState>((state) => state.alerts);

// Use entity adapter selector directly - it's already memoized
export const selectAlerts = alertsSelectors.selectAll;

export const selectAlertById = (id: string) => (state: RootState) => alertsSelectors.selectById(state, id);

export const selectBackendErrorAlerts = createSelector(selectAlerts, filter(isBackendErrorAlert));

const selectAlertContext = (_state: RootState, context: AlertContext) => context;

export const selectSnackBarAlertsByContext = createSelector(selectAlertContext, selectAlerts, (context, alerts) =>
  alerts.filter(isSnackBarAlert).filter(isAlertMatchingContext(context))
);

/** The alerts the DS 2.0 toasts draw, in store order: snackbars, and backend errors (which are always APP). */
export const selectToastAlertsByContext = createSelector(selectAlertContext, selectAlerts, (context, alerts) =>
  alerts.filter((alert) => isSnackBarAlert(alert) || isBackendErrorAlert(alert)).filter(isAlertMatchingContext(context))
);

const selectDialogAlerts = createSelector(selectAlerts, filter(isDialogAlert));

export const selectLastDialogAlertByContext = createSelector(
  selectAlertContext,
  selectDialogAlerts,
  (context, alerts) => first(alerts.filter(isAlertMatchingContext(context)))
);

export const selectLastBackendErrorAlert = createSelector(selectBackendErrorAlerts, last);
